(() => {
  if (window.adiosFrizzInitialized) return;
  window.adiosFrizzInitialized = true;

  function initialize(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const cleanup = [];
    const controller = new AbortController();
    const signal = controller.signal;

    // Swiper is already provided by the theme layout. Keep each reference's breakpoints.
    root.querySelectorAll('[data-adios-type="thumbnails"], [data-adios-type="slider"]').forEach((element) => {
      const config = root.querySelector(`#instant-slider-${element.id}-params`);
      if (!config || !window.Swiper) return;
      const params = JSON.parse(config.textContent);
      if (reducedMotion.matches) {
        Object.values(params.breakpoints).forEach((breakpoint) => { breakpoint.speed = 0; });
      }
      if (params.navigation) {
        params.navigation.nextEl = root.querySelector(params.navigation.nextEl);
        params.navigation.prevEl = root.querySelector(params.navigation.prevEl);
      }
      if (params.pagination) {
        params.pagination.el = root.querySelector(params.pagination.el);
        const bulletClass = params.pagination.el?.firstElementChild?.className || '';
        params.pagination.renderBullet = (index, className) =>
          `<button type="button" class="${className} ${bulletClass}" aria-label="Go to slide ${index + 1}"></button>`;
      }
      params.a11y = { enabled: true };
      if (params.thumbs) {
        const thumbElement = root.querySelector('[data-adios-type="thumbnails"][data-adios-slider-id="' + element.dataset.adiosSliderId + '"]');
        if (thumbElement && !thumbElement.swiper) {
          const thumbConfig = root.querySelector('#instant-slider-' + thumbElement.id + '-params');
          const thumbParams = JSON.parse(thumbConfig.textContent);
          thumbParams.watchSlidesProgress = true;
          thumbParams.slideToClickedSlide = true;
          if (reducedMotion.matches) Object.values(thumbParams.breakpoints).forEach(point => { point.speed = 0; });
          const thumbs = new window.Swiper(thumbElement, thumbParams);
          cleanup.push(() => thumbs.destroy(true, true));
        }
        if (thumbElement?.swiper) params.thumbs.swiper = thumbElement.swiper;
      }
      if (element.swiper) return;
      const slider = new window.Swiper(element, params);
      cleanup.push(() => slider.destroy(true, true));
    });

    root.querySelectorAll('[data-adios-type="thumbnails"]').forEach((element) => {
      const gallery = root.querySelector('[data-adios-type="slider"][data-adios-slider-id="' + element.dataset.adiosSliderId + '"]')?.swiper;
      if (!gallery) return;
      const thumbs = [...element.querySelectorAll('.instant-slider-slide')];
      const update = () => thumbs.forEach((thumb, index) => thumb.setAttribute('aria-pressed', String(index === gallery.activeIndex)));
      thumbs.forEach((thumb, index) => {
        thumb.setAttribute('role', 'button');
        thumb.tabIndex = 0;
        thumb.setAttribute('aria-label', 'Show product image ' + (index + 1));
        thumb.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          gallery.slideTo(index);
        }, { signal });
      });
      gallery.on('slideChange', update);
      cleanup.push(() => gallery.off('slideChange', update));
      update();
    });

    const reveals = root.querySelectorAll('.instant-scroll-trigger');
    if (!reducedMotion.matches && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (!isIntersecting) return;
          target.classList.remove('instant-scroll-trigger--hidden');
          observer.unobserve(target);
        });
      }, { threshold: 0.05 });
      reveals.forEach((element) => {
        element.classList.add('instant-scroll-trigger--hidden');
        observer.observe(element);
      });
      cleanup.push(() => observer.disconnect());
    }

    root.querySelectorAll('[data-adios-type="ticker"]').forEach((ticker) => {
      const tracks = ticker.querySelectorAll(':scope > .instant-ticker');
      const original = tracks[0].firstElementChild;
      const populate = () => {
        tracks.forEach((track) => track.replaceChildren());
        tracks[0].append(original);
        const count = Math.max(1, Math.ceil(ticker.clientWidth / Math.max(original.offsetWidth, 1)));
        for (let index = 1; index < count; index++) {
          const copy = original.cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          tracks[0].append(copy);
        }
        tracks[1].innerHTML = tracks[0].innerHTML;
        tracks[1].setAttribute('aria-hidden', 'true');
        const gap = parseFloat(getComputedStyle(ticker).columnGap) || 0;
        ticker.style.setProperty('--gap', `${gap}px`);
        ticker.style.setProperty('--duration', `${(tracks[0].offsetWidth + gap) / Number(ticker.dataset.adiosTickerSpeed || 20)}s`);
        ticker.style.setProperty('--direction', 'normal');
        ticker.style.setProperty('--play', 'running');
      };
      populate();
      document.fonts.ready.then(() => { if (root.isConnected) populate(); });
      const observer = new ResizeObserver(populate);
      observer.observe(ticker);
      cleanup.push(() => observer.disconnect());
    });

    root.querySelectorAll('[data-adios-type="accordion-container"]').forEach((container, group) => {
      const items = [...container.querySelectorAll('[data-adios-type="accordion-item"]')];
      const multiple = container.dataset.isMultiOpenEnabled === 'true';
      const updates = [];
      items.forEach((item, index) => {
        const trigger = item.querySelector('[data-adios-type="accordion-header"]');
        const content = item.querySelector('[data-adios-type="accordion-content"]');
        const id = 'AdiosFAQ-' + root.dataset.sectionId + '-' + group + '-' + index;
        trigger.id = id + '-trigger';
        content.id = id + '-content';
        trigger.setAttribute('aria-controls', content.id);
        content.setAttribute('aria-labelledby', trigger.id);
        item.dataset.state = index === 0 && container.dataset.isFirstOpen === 'true' ? 'open' : 'closed';
        const update = () => {
          const open = item.dataset.state === 'open';
          trigger.setAttribute('aria-expanded', String(open));
          content.inert = !open;
          content.setAttribute('aria-hidden', String(!open));
          content.style.setProperty('--instant-accordion-content-height', content.scrollHeight + 'px');
        };
        updates.push(update);
        trigger.addEventListener('click', () => {
          const opening = item.dataset.state !== 'open';
          if (opening && !multiple) items.forEach((other) => { other.dataset.state = 'closed'; });
          item.dataset.state = opening ? 'open' : 'closed';
          updates.forEach((refresh) => refresh());
        }, { signal });
        update();
        const observer = new ResizeObserver(update);
        Array.from(content.children).forEach((child) => observer.observe(child));
        cleanup.push(() => observer.disconnect());
      });
    });

    const header = document.querySelector('sticky-header')?.closest('.section-header');
    const updateHeaderOffset = () => {
      const height = header?.getBoundingClientRect().height || 0;
      root.style.setProperty('--adios-header-offset', `${height}px`);
      return height;
    };
    updateHeaderOffset();
    if (header) {
      const observer = new ResizeObserver(updateHeaderOffset);
      observer.observe(header);
      cleanup.push(() => observer.disconnect());
    }

    root.addEventListener('click', (event) => {
      const anchor = event.target.closest('a[href^="#"]');
      if (!anchor || anchor.hash.length < 2) return;
      const target = document.getElementById(anchor.hash.slice(1));
      if (!target || !root.contains(target)) return;
      event.preventDefault();
      const offset = updateHeaderOffset();
      const top = window.scrollY + target.getBoundingClientRect().top - offset;
      window.scrollTo({ top, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      history.replaceState(null, '', anchor.hash);
    }, { signal });

    root.addEventListener('adios-frizz:unload', () => {
      controller.abort();
      cleanup.forEach((dispose) => dispose());
    }, { once: true });
  }

  const initializeAll = () => document.querySelectorAll('[data-adios-frizz]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector('[data-adios-frizz]')?.dispatchEvent(new Event('adios-frizz:unload'));
  });
})();
